import { throttle } from 'lodash-es';
import type { StringValue } from 'ms';
import ms from 'ms';
import type { StringDocumentNode } from '@soundxyz/gql-string';
import type { NonEmptyPlural } from '@soundxyz/utils/const';

import type { ExecutionResultWithData } from '@graphql/graphql-ws';
import { SubscriptionEffects } from '@graphql/graphql-ws';
import { invalidateOperations, ReactQueryEffects } from '@graphql/reactQuery';

type EffectCallbackParameter = {
  operation: StringDocumentNode;
  result: ExecutionResultWithData<Record<string, unknown>>;
  variables?: Record<string, unknown>;
};

type RefetchOperationsOnCompleteParameters = {
  trigger: NonEmptyPlural<StringDocumentNode>;
  refetch: NonEmptyPlural<StringDocumentNode>;
  filter?: (data: EffectCallbackParameter) => unknown;
  throttled?: StringValue;
} & Omit<Parameters<typeof invalidateOperations>[0], 'operations'>;

export function RefetchOperationsOnComplete({
  trigger,
  refetch,
  throttled,
  filter,
  ...invalidateOptions
}: RefetchOperationsOnCompleteParameters) {
  const triggerList = Array.isArray(trigger) ? trigger : ([trigger] as const);
  const refetchList = Array.isArray(refetch) ? refetch : ([refetch] as const);

  function invalidate() {
    invalidateOperations({
      operations: refetchList,
      ...invalidateOptions,
    });
  }

  const refetchCallback = throttled
    ? throttle(invalidate, ms(throttled), {
        leading: true,
        trailing: true,
      })
    : invalidate;

  function handleEffect(param: EffectCallbackParameter) {
    if (filter && !filter(param)) return;

    return refetchCallback();
  }

  const unsubscriptions = new Set<() => void>();

  for (const triggerOperation of triggerList) {
    unsubscriptions.add(ReactQueryEffects.onCompleted(triggerOperation, handleEffect));

    unsubscriptions.add(SubscriptionEffects.onCompleted(triggerOperation, handleEffect));
  }

  return function unsubscribe() {
    for (const effectUnsubscribe of unsubscriptions) {
      effectUnsubscribe();
    }
    unsubscriptions.clear();
  };
}
